import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, type components, isApiError } from '@saay/api-client';
import { formatTimestamp } from '@saay/formatting';
import {
  applyFieldErrors,
  Banner,
  Button,
  CommandFeedback,
  Dialog,
  ErrorState,
  FactList,
  FieldErrorList,
  MenuItem,
  TextArea,
} from '../kit.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { LoginLockClearance, LoginLockClearanceRequest } from '../api.js';
import { canUse } from '../permissions.js';

type LoginLockState = components['schemas']['LoginLockState'];
import { CLEARANCE_POLL_LIMIT_MS, clearancePath, useLoginLockClearance } from '../queries.js';
import { useSubmitBlocked } from './form.js';

/**
 * Support unlock of login locks (D-51, AC §18.21 L1–L2), from the buyer detail, a merchant staff row,
 * and (Super Admin) a platform role row. After verifying the person, an admin with
 * `IDENTITY_LOGIN_LOCK_CLEAR` (Super, Operations) records how they verified them; the API accepts the
 * request (`202`), the worker clears the locks, and this dialog polls the clearance every 2 s for up
 * to 30 s, then offers a manual refresh. The request never carries or shows the mobile number.
 */

const NOTE_MAX = 500;

const noteSchema = z.object({
  verificationNote: z
    .string()
    .trim()
    .min(1, 'روش تأیید هویت فرد را بنویسید.')
    .max(NOTE_MAX, 'حداکثر ۵۰۰ کاراکتر.'),
});

const FAILURE_FA: Readonly<Record<string, string>> = {
  KEYCLOAK_USER_NOT_FOUND: 'حساب ورود این کاربر پیدا نشد.',
  PROVIDER_UNAVAILABLE: 'سرویس ورود در دسترس نبود.',
};

function lockLine(wasActive: boolean): string {
  return wasActive ? 'فعال بود و برداشته شد' : 'فعال نبود';
}

function ClearanceStatus({
  clearanceId,
  onRetry,
  startedAt,
  userId,
}: {
  readonly clearanceId: string;
  readonly onRetry: () => void;
  readonly startedAt: number;
  readonly userId: string;
}) {
  const clearance = useLoginLockClearance(userId, clearanceId, startedAt);
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), CLEARANCE_POLL_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [startedAt]);

  if (clearance.status === 'error') {
    return <ErrorState error={clearance.error} onRetry={() => void clearance.refetch()} />;
  }
  const data: LoginLockClearance | undefined = clearance.data;
  if (data === undefined || data.status === 'REQUESTED') {
    return (
      <section aria-live="polite" className="saay-panel" role="status">
        <h2 className="saay-panel__title">در حال رفع قفل ورود…</h2>
        <p className="saay-panel__text">
          {timedOut
            ? 'نتیجه هنوز نرسیده است. چند لحظه بعد دوباره بررسی کنید.'
            : 'درخواست ثبت شد؛ نتیجه چند ثانیهٔ دیگر نمایش داده می‌شود.'}
        </p>
        {timedOut ? (
          <Button
            loading={clearance.isFetching}
            onClick={() => void clearance.refetch()}
            variant="secondary"
          >
            بررسی دوباره
          </Button>
        ) : null}
      </section>
    );
  }
  if (data.status === 'FAILED') {
    return (
      <>
        <Banner tone="danger">
          {`رفع قفل انجام نشد: ${data.failureCode === null ? 'خطای نامشخص' : (FAILURE_FA[data.failureCode] ?? 'خطای نامشخص')}`}
        </Banner>
        <Button onClick={onRetry} variant="secondary">
          درخواست دوباره
        </Button>
      </>
    );
  }
  return (
    <section aria-live="polite" className="admin-sheet-stack" role="status">
      <Banner tone="success">
        قفل‌های ورود این کاربر برداشته شد و برای او پیامک ارسال می‌شود.
      </Banner>
      <FactList
        items={[
          ...(data.outcome === null
            ? []
            : [
                {
                  key: 'sms',
                  label: 'قفل ورود پیامکی',
                  value: lockLine(data.outcome.smsLockWasActive),
                },
                {
                  key: 'password',
                  label: 'قفل رمز عبور',
                  value: lockLine(data.outcome.passwordLockWasActive),
                },
              ]),
          ...(data.completedAt === null
            ? []
            : [
                {
                  key: 'completed',
                  label: 'زمان انجام',
                  value: (
                    <time dateTime={data.completedAt}>{formatTimestamp(data.completedAt)}</time>
                  ),
                },
              ]),
          {
            key: 'by',
            label: 'درخواست‌کننده',
            value: data.requestedBy.displayNameFa ?? 'بدون نام',
          },
        ]}
      />
    </section>
  );
}

function ClearanceDialog({
  onOpenChange,
  personLabel,
  userId,
}: {
  readonly onOpenChange: (open: boolean) => void;
  readonly personLabel: string;
  readonly userId: string;
}) {
  const offline = useSubmitBlocked();
  const [tracked, setTracked] = useState<{ id: string; startedAt: number } | null>(null);
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<{ verificationNote: string }>({
    defaultValues: { verificationNote: '' },
    resolver: zodResolver(noteSchema),
  });
  const command = useCommand<LoginLockClearanceRequest, LoginLockClearance>(
    'identity.login-lock.clear',
    (client, body, intent) => client.post(clearancePath(userId), { body, intent }),
    {
      onSuccess: (result) => {
        setTracked({ id: result.data.id, startedAt: Date.now() });
      },
    },
  );

  function handle(outcome: CommandOutcome<LoginLockClearance>): void {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(applyFieldErrors(outcome.error, ['verificationNote'], form.setError));
    // A clearance of this identity is still running: follow it instead of starting another.
    const pending = outcome.error.details?.clearanceId;
    if (outcome.error.code === 'LOGIN_LOCK_CLEARANCE_PENDING' && typeof pending === 'string') {
      setTracked({ id: pending, startedAt: Date.now() });
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const pendingElsewhere =
    isApiError(error) && error.code === 'LOGIN_LOCK_CLEARANCE_PENDING' && tracked !== null;

  return (
    <Dialog
      description="پس از تأیید هویت فرد (مثلاً تماس تلفنی و تطبیق کد ملی)، قفل‌های ورود پیامکی و رمز عبور او برداشته می‌شود. هر رفع قفل در حسابرسی ثبت می‌شود."
      eyebrow={personLabel}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="رفع قفل ورود"
    >
      {tracked === null ? (
        <form
          className="admin-form"
          noValidate
          onSubmit={form.handleSubmit(async (values) => {
            setUnmapped([]);
            handle(await command.submit({ verificationNote: values.verificationNote.trim() }));
          })}
        >
          <TextArea
            error={form.formState.errors.verificationNote?.message}
            hint="مثلاً «تماس تلفنی و تطبیق کد ملی و تاریخ تولد»"
            label="روش تأیید هویت"
            maxLength={NOTE_MAX}
            {...form.register('verificationNote')}
          />
          <FieldErrorList errors={unmapped} />
          <CommandFeedback
            onCheckStatus={() => void command.checkStatus().then(handle)}
            state={command.state}
          />
          <Button
            disabled={offline.blocked}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            رفع قفل ورود
          </Button>
        </form>
      ) : (
        <>
          {pendingElsewhere ? (
            <Banner tone="info">
              درخواست رفع قفل دیگری برای این کاربر در حال انجام است؛ نتیجهٔ همان نمایش داده می‌شود.
            </Banner>
          ) : null}
          <ClearanceStatus
            clearanceId={tracked.id}
            onRetry={() => {
              // A new request is a new intent, so it gets a new key (AC §18.21).
              command.reset();
              form.reset();
              setTracked(null);
            }}
            startedAt={tracked.startedAt}
            userId={userId}
          />
        </>
      )}
    </Dialog>
  );
}

/**
 * The «رفع قفل ورود» action (gated by `IDENTITY_LOGIN_LOCK_CLEAR`; the API also answers `404` to an
 * Operations Admin for a platform-role holder, which the dialog shows).
 */
export function LoginLockAction({
  loginLock,
  personLabel,
  presentation = 'button',
  self,
  userId,
}: {
  readonly loginLock?: LoginLockState;
  readonly personLabel: string;
  readonly presentation?: 'button' | 'menu';
  readonly self: AdminSelf;
  readonly userId: string;
}) {
  const [open, setOpen] = useState(false);
  if (!canUse(self, 'requestLoginLockClearance')) {
    return null;
  }
  const visible = loginLock === undefined ? presentation === 'menu' : loginLock.locked === true;
  if (!visible) {
    return null;
  }
  const trigger =
    presentation === 'menu' ? (
      <MenuItem onSelect={() => setOpen(true)}>رفع قفل ورود</MenuItem>
    ) : (
      <Button onClick={() => setOpen(true)} size="sm" variant="ghost">
        رفع قفل ورود
      </Button>
    );
  return (
    <>
      {trigger}
      {open ? (
        <ClearanceDialog onOpenChange={setOpen} personLabel={personLabel} userId={userId} />
      ) : null}
    </>
  );
}
